import { Helmet } from '@dr.pogodin/react-helmet';
import {
  ArrowDownIcon,
  PlusIcon,
  SlidersHorizontalIcon,
  XIcon,
} from '@phosphor-icons/react';
import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { css, html } from 'react-strict-dom';
import { color } from 'src/constants/color.css';

import { Api } from '../../../api';
import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuItem,
  IconButton,
  PageError,
  PageSkeleton,
  PageWrap,
  Pagination,
  RouterLink,
  Spacer,
  Stack,
  Tabs,
  Text,
} from '../../../components';
import { Color, Style } from '../../../constants';
import { useClientData, useCompany } from '../../../contexts/ClientDataContext';
import { useMyUserClient } from '../../../hooks';
import { entries, values } from '../../../util';
import CompanyAgencyControls from '../components/CompanyAgencyControls';
import FilterChip from '../components/FilterChip';
import MaintenanceTicketsTabs from '../components/MaintenanceTicketsTabs';
import MobileFilterModal from '../components/MobileFilterModal';
import MobileSortSheet from '../components/MobileSortSheet';
import SearchInput from '../components/SearchInput';
import TicketTable from '../components/TicketTable';
import { buildSearchParams, getLabelGroups, getSearchParams } from '../util';

type SuggestionColorScheme = 'infoNeeded' | 'withResolution';

const SUGGESTION_COLORS: Record<
  SuggestionColorScheme,
  { bg: string; border: string; text: string }
> = {
  infoNeeded: { bg: '#FFDCDA', border: '#F5222D', text: '#CF1322' },
  withResolution: { bg: '#f0FaFF', border: '#91D5FF', text: '#1890FF' },
};

function SuggestionBanner({
  colorScheme,
  icon,
  title,
  children,
  detail,
}: {
  colorScheme: SuggestionColorScheme;
  icon: string;
  title: string;
  children: React.ReactNode;
  detail?: string;
}) {
  const colors = SUGGESTION_COLORS[colorScheme];
  return (
    <html.div
      style={[
        Style.row,
        Style.alignStart,
        styles.suggestion,
        styles.suggestionColors(colors.bg, colors.border, colors.text),
      ]}
    >
      <html.span style={styles.suggestionIcon}>
        <i className={icon} />
      </html.span>
      <html.div>
        <Text as="h2" style={styles.suggestionTitle}>
          {title}
        </Text>
        <Text as="p" style={styles.suggestionBody}>
          {children}
        </Text>
        {detail && (
          <Text as="p" style={[styles.suggestionBody, styles.suggestionDetail]}>
            {detail}
          </Text>
        )}
      </html.div>
    </html.div>
  );
}

export default function TicketsPage() {
  const { companySlug } = useParams<{ companySlug: string }>();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const company = useCompany(companySlug!);
  const myUser = useMyUserClient();
  const { ticketLabels } = useClientData();
  const labelGroups = React.useMemo(
    () => getLabelGroups(company, ticketLabels),
    [company, ticketLabels]
  );

  const searchParams = getSearchParams(search);
  const showClosed = searchParams.status === 'closed';

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [search]);

  const activeLabelIds = values(searchParams.filters).flat();

  const apiTicket = Api.Ticket.useReadCollection({
    company: company['@id'],
    ...(searchParams.status !== 'all'
      ? { 'exists[closedAt]': showClosed }
      : {}),
    ...(searchParams.sort ? { [`order[${searchParams.sort}]`]: 'desc' } : {}),
    ...(searchParams.search ? { search: searchParams.search } : {}),
    ...(activeLabelIds.length > 0
      ? { 'labels[]': activeLabelIds.map((id) => `/ticket-labels/${id}`) }
      : {}),
    page: searchParams.page,
  } as Parameters<typeof Api.Ticket.useReadCollection>[0]);

  const isSearching = !!searchParams.search;

  const apiStats = Api.CompanyTicketStats.useReadItemTicketStats(company.id);

  const [mobileSortOpen, setMobileSortOpen] = React.useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = React.useState(false);

  if (apiTicket.error) return <PageError {...apiTicket} />;
  if (apiStats.error) return <PageError {...apiStats} />;
  if (!apiStats.data) return <PageSkeleton variant="tickets-list" />;
  const tickets = apiTicket.data?.['hydra:member'] ?? [];
  const totalItems = apiTicket.data?.['hydra:totalItems'] ?? 0;

  const SORT_LABELS: Record<string, string> = {
    '': 'Relevance',
    createdAt: 'Newest',
    updatedAt: 'Last updated',
    closedAt: 'Recently closed',
  };
  const sortToLabel = (sort: string) => SORT_LABELS[sort] ?? sort;

  const sortOptions = isSearching
    ? ['', 'createdAt', 'updatedAt']
    : ['createdAt', 'updatedAt'];
  if (searchParams.status === 'closed') sortOptions.push('closedAt');

  const filterableGroups = values(labelGroups).filter((g) => g.id !== 'other');
  const hasActiveFilters = Object.keys(searchParams.filters).length > 0;
  const activeFilterCount = Object.keys(searchParams.filters).length;

  const navigateWith = (
    overrides: Partial<ReturnType<typeof getSearchParams>>
  ) => {
    navigate(
      `${pathname}?${buildSearchParams({ ...searchParams, ...overrides })}`
    );
  };

  return (
    <PageWrap background={Color.neutral5} noPadding limitWidth="large">
      <Helmet>
        <title>{`Tickets | ${company.name}`}</title>
      </Helmet>

      <Stack padH={1.5} padV={1.5}>
        <MaintenanceTicketsTabs companySlug={companySlug!} selected="tickets" />
        <Spacer size={1.5} />

        {myUser.agencyRole && (
          <>
            <CompanyAgencyControls company={company} />
            <Spacer />
          </>
        )}

        <html.div
          style={[Style.row, Style.alignStart, Style.wrap, styles.suggestions]}
        >
          {apiStats.data.numOpenInfoNeeded > 0 && (
            <SuggestionBanner
              colorScheme="infoNeeded"
              icon="fa-solid fa-question"
              title="Info needed"
              detail="Tickets in this state for 30 days will be auto-closed."
            >
              You have{' '}
              <Text as="b" bold style={styles.suggestionBold}>
                {apiStats.data.numOpenInfoNeeded}
              </Text>{' '}
              open tickets where we need information to proceed. Please provide
              this for work to progress on the ticket.
            </SuggestionBanner>
          )}

          {apiStats.data.numOpenWithResolution > 0 && (
            <SuggestionBanner
              colorScheme="withResolution"
              icon="fa-solid fa-check"
              title="Resolution reached"
              detail="Tickets in this state for 30 days will be auto-closed."
            >
              You have{' '}
              <Text as="b" bold style={styles.suggestionBold}>
                {apiStats.data.numOpenWithResolution}
              </Text>{' '}
              open tickets which have been resolved. Please close the ticket if
              you're happy with the resolution, or if not, comment letting us
              know why.
            </SuggestionBanner>
          )}
        </html.div>
        <Spacer size={1} />

        <Stack style={styles.tableContainer}>
          <Stack
            style={[
              Style.row,
              Style.alignCenter,
              Style.spaceBetween,
              Style.wrap,
              styles.tabBar,
            ]}
            gap={0.5}
          >
            <html.div style={[Style.row, Style.alignCenter, styles.tabLeft]}>
              {isSearching ? (
                <Stack style={styles.checkboxRow}>
                  <Stack style={[Style.row, Style.alignCenter]} gap={0.75}>
                    <html.div style={styles.checkboxContainer}>
                      <Checkbox
                        label="Open Tickets"
                        checked={
                          searchParams.status === 'open' ||
                          searchParams.status === 'all'
                        }
                        onChange={() => {
                          const next =
                            searchParams.status === 'closed' ? 'all' : 'closed';
                          navigateWith({ status: next, page: 1 });
                        }}
                      />
                    </html.div>
                    <html.div style={styles.checkboxContainer}>
                      <Checkbox
                        label="Closed Tickets"
                        checked={
                          searchParams.status === 'closed' ||
                          searchParams.status === 'all'
                        }
                        onChange={() => {
                          const next =
                            searchParams.status === 'open' ? 'all' : 'open';
                          navigateWith({ status: next, page: 1 });
                        }}
                      />
                    </html.div>
                  </Stack>
                </Stack>
              ) : (
                <Tabs
                  accentColor={Color.accent}
                  tabs={[
                    {
                      key: 'open',
                      label: 'Open',
                      count: totalItems,
                      to: `${pathname}?${buildSearchParams({
                        ...searchParams,
                        status: 'open',
                        page: 1,
                      })}`,
                      selected: !showClosed,
                    },
                    {
                      key: 'closed',
                      label: 'Closed',
                      count: totalItems,
                      to: `${pathname}?${buildSearchParams({
                        ...searchParams,
                        status: 'closed',
                        page: 1,
                      })}`,
                      selected: showClosed,
                    },
                  ]}
                />
              )}
            </html.div>

            <html.div
              style={[
                Style.row,
                Style.alignCenter,
                Style.wrap,
                styles.controlsArea,
              ]}
            >
              {/* Desktop sort/filter dropdowns */}
              <html.div
                style={[Style.row, Style.alignCenter, styles.desktopSortFilter]}
              >
                <DropdownMenu
                  header="Sort by..."
                  trigger={(open) => (
                    <IconButton
                      label={sortToLabel(searchParams.sort)}
                      selected={open}
                    >
                      <ArrowDownIcon size={20} />
                    </IconButton>
                  )}
                >
                  {sortOptions.map((choice) => (
                    <DropdownMenuItem
                      key={choice}
                      selected={searchParams.sort === choice}
                      onClick={() => navigateWith({ sort: choice, page: 1 })}
                    >
                      {sortToLabel(choice)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenu>

                <DropdownMenu
                  header="Filter by..."
                  trigger={(open) => (
                    <IconButton tooltip="Filter" selected={open}>
                      <SlidersHorizontalIcon size={20} />
                    </IconButton>
                  )}
                >
                  {filterableGroups.map((group) => {
                    const isActive = group.id in searchParams.filters;
                    return (
                      <DropdownMenuItem
                        key={group.id}
                        showRadio={false}
                        selected={isActive}
                        onClick={() => {
                          const next = { ...searchParams.filters };
                          if (isActive) {
                            delete next[group.id];
                          } else {
                            next[group.id] = [];
                          }
                          navigateWith({ filters: next, page: 1 });
                        }}
                      >
                        {group.name}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenu>
              </html.div>

              {/* Mobile sort/filter buttons */}
              <html.div
                style={[Style.row, Style.alignCenter, styles.mobileSortFilter]}
              >
                <IconButton
                  label={sortToLabel(searchParams.sort)}
                  onClick={() => setMobileSortOpen(true)}
                >
                  <ArrowDownIcon size={20} />
                </IconButton>
                <IconButton
                  label={
                    activeFilterCount > 0
                      ? String(activeFilterCount)
                      : undefined
                  }
                  onClick={() => setMobileFilterOpen(true)}
                >
                  <SlidersHorizontalIcon size={20} />
                </IconButton>
              </html.div>

              <html.div style={styles.searchWrap}>
                <SearchInput
                  value={searchParams.search}
                  onChange={(value) => {
                    navigateWith({
                      search: value,
                      status: value ? 'all' : 'open',
                      sort: value ? '' : 'createdAt',
                      page: 1,
                    });
                  }}
                />
              </html.div>

              <html.div style={styles.newTicketDesktop}>
                <Button
                  label="New ticket"
                  to={`/company/${companySlug}/tickets/new`}
                  size="small"
                  iconLeft={(props) => <PlusIcon {...props} />}
                />
              </html.div>
              <RouterLink to={`/company/${companySlug}/tickets/new`}>
                <html.div
                  style={[Style.row, Style.centerBoth, styles.newTicketMobile]}
                >
                  <PlusIcon size={24} color="#fff" />
                </html.div>
              </RouterLink>
            </html.div>
          </Stack>

          {hasActiveFilters && (
            <html.div
              style={[
                Style.row,
                Style.alignCenter,
                Style.wrap,
                styles.filterBar,
              ]}
            >
              {entries(searchParams.filters).map(([groupId, selectedIds]) => {
                const group = labelGroups[groupId];
                if (!group) return null;
                return (
                  <FilterChip
                    key={groupId}
                    category={group.name}
                    options={group.labels.map((l) => ({
                      id: l.id,
                      name: l.name,
                    }))}
                    selectedIds={selectedIds}
                    onToggle={(id) => {
                      const current = searchParams.filters[groupId] ?? [];
                      const next = current.includes(id)
                        ? current.filter((v) => v !== id)
                        : [...current, id];
                      navigateWith({
                        filters: { ...searchParams.filters, [groupId]: next },
                        page: 1,
                      });
                    }}
                  />
                );
              })}
              <Button
                label="Clear all"
                iconLeft={(props) => (
                  <XIcon {...props} color={Color.neutral100} />
                )}
                variant="text"
                onClick={() => navigateWith({ filters: {}, page: 1 })}
              />
            </html.div>
          )}

          <Stack gap={0.5}>
            <TicketTable
              tickets={tickets}
              companySlug={companySlug!}
              labelGroups={labelGroups}
              searchQuery={searchParams.search}
              isLoading={!apiTicket.data}
              invalidate={apiTicket.invalidate}
            />
            {apiTicket.data && (
              <Stack style={Style.alignStart}>
                <Pagination
                  hydraCollection={apiTicket.data}
                  pageUrl={(n) =>
                    `${pathname}?${buildSearchParams({
                      ...searchParams,
                      page: n,
                    })}`
                  }
                />
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
      {mobileSortOpen && (
        <MobileSortSheet
          options={sortOptions.map((s) => ({
            value: s,
            label: sortToLabel(s),
          }))}
          selected={searchParams.sort}
          onSelect={(sort) => navigateWith({ sort, page: 1 })}
          onClose={() => setMobileSortOpen(false)}
        />
      )}
      {mobileFilterOpen && (
        <MobileFilterModal
          groups={filterableGroups}
          initialFilters={searchParams.filters}
          onApply={(filters) => navigateWith({ filters, page: 1 })}
          onClose={() => setMobileFilterOpen(false)}
        />
      )}
    </PageWrap>
  );
}

const styles = css.create({
  suggestions: {
    gap: 8,
  },
  suggestion: {
    paddingInline: 15,
    paddingBlock: 12,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderRadius: 12,
    maxWidth: 410,
  },
  suggestionColors: (bg: string, border: string, text: string) => ({
    backgroundColor: bg,
    borderColor: border,
    color: text,
  }),
  suggestionIcon: {
    fontSize: 24,
    marginInlineEnd: 12,
    opacity: 0.8,
    marginTop: 5,
  },
  suggestionTitle: {
    fontWeight: '400',
    fontSize: 18,
    margin: 0,
  },
  suggestionBody: {
    marginBlockStart: 5,
    marginBlockEnd: 0,
    marginInline: 0,
    fontSize: 13,
    opacity: 0.8,
  },
  suggestionDetail: {
    fontSize: 11,
    opacity: 0.7,
  },
  suggestionBold: {
    fontSize: 13,
  },
  tableContainer: {
    gap: {
      default: 24,
      '@media (max-width: 768px)': 16,
    },
  },
  checkboxRow: {
    width: {
      default: 'auto',
      '@media (max-width: 768px)': '100%',
    },
  },
  checkboxContainer: {
    paddingTop: 10,
    paddingBottom: 14,
    paddingInline: 8,
  },
  tabLeft: {
    width: {
      default: 'auto',
      '@media (max-width: 768px)': '100%',
    },
    borderBottomWidth: {
      default: 0,
      '@media (max-width: 768px)': 1,
    },
    borderBottomStyle: 'solid',
    borderBottomColor: color.neutral20,
  },
  tabBar: {
    // on mobile, divider is under the checkbox row
    borderBottomWidth: {
      default: 1,
      '@media (max-width: 768px)': 0,
    },
    borderBottomStyle: 'solid',
    borderBottomColor: color.neutral20,
  },
  controlsArea: {
    gap: 8,
    width: {
      default: 'auto',
      '@media (max-width: 768px)': '100%',
    },
  },
  searchWrap: {
    order: {
      default: 0,
      '@media (max-width: 768px)': 3,
    },
    width: {
      default: 'auto',
      '@media (max-width: 768px)': '100%',
    },
  },
  newTicketDesktop: {
    display: {
      default: 'flex',
      '@media (max-width: 768px)': 'none',
    },
  },
  desktopSortFilter: {
    display: {
      default: 'flex',
      '@media (max-width: 768px)': 'none',
    },
    gap: 4,
  },
  mobileSortFilter: {
    display: {
      default: 'none',
      '@media (max-width: 768px)': 'flex',
    },
    gap: 4,
  },
  newTicketMobile: {
    display: {
      default: 'none',
      '@media (max-width: 768px)': 'flex',
    },
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: color.accent,
    order: 2,
    marginInlineStart: 'auto',
  },
  filterBar: {
    gap: 8,
  },
  clearAll: {
    gap: 4,
    paddingInline: 8,
    paddingBlock: 4,
    borderWidth: 0,
    borderRadius: 6,
    backgroundColor: 'transparent',
    color: color.accent,
    fontSize: 14,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    ':hover': {
      backgroundColor: color.accentBg,
    },
  },
  countBadge: {
    marginInlineStart: 6,
    paddingInline: 7,
    paddingBlock: 1,
    borderRadius: 10,
    fontSize: 12,
    fontWeight: '600',
    backgroundColor: color.neutral10,
    color: color.neutral60,
  },
});
